import { V3_API_URL } from 'views/TradingCompetitionV3/config'

/** The CADINU social API lives next to the competition API (stage-api on Stage, api.cadinu.io later). */
export const SOCIAL_API = process.env.NEXT_PUBLIC_SOCIAL_API_URL || `${new URL(V3_API_URL).origin}/api/social`

export interface Avatar { collection: string; token_id: string; level: 1 | 2 | null; character: string | null; image: string | null; source?: string }
export interface Achievement { id: string; title: string; description: string; earned: boolean }
/** Blue "Token team" tick: the wallet confirmed its role by signature and CADINU approved the project's info. */
export interface TeamBadge { token: string; symbol: string; role: string; roleLabel: string }
export interface Profile {
  state: 'on' | 'off' | 'none'
  address: string
  handle?: string | null
  avatar?: Avatar | null
  complete?: boolean
  joined?: string | null
  followers?: number
  following?: number
  achievements?: Achievement[]
  show_pnl?: boolean
  cover?: string | null
  referral?: { id: string; signups: number }
  /** the inviter bound at the first CADINU sign-in (permanent); owner view only */
  invited_by?: { address: string; handle: string | null } | null
  viewer_follows?: boolean
  is_me?: boolean
  /** gold "Official CADINU account" tick (our own accounts, set in the admin); public profiles only */
  official?: boolean
  /** blue "Token team" ticks, at most two */
  teams?: TeamBadge[]
  settings?: { visible: boolean; show_pnl: boolean; handle_change_after: string | null; cover: string | null }
}
export interface Person { state: 'on' | 'off'; address: string; handle?: string | null; avatar?: Avatar | null; official?: boolean; teams?: TeamBadge[] }

const KEY = 'cadinu.social.session'
export interface Session { address: string; token: string; expiry: string }

export function loadSession(address?: string): Session | null {
  try {
    const s = JSON.parse(window.localStorage.getItem(KEY) || 'null') as Session | null
    if (!s || !address || s.address.toLowerCase() !== address.toLowerCase() || new Date(s.expiry).getTime() < Date.now() + 60_000) return null
    return s
  } catch {
    return null
  }
}
export function saveSession(s: Session | null) {
  try {
    if (s) window.localStorage.setItem(KEY, JSON.stringify(s))
    else window.localStorage.removeItem(KEY)
  } catch {
    /* private mode: the session lives in memory only */
  }
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, public body: Record<string, unknown> = {}) {
    super(code)
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown; token?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (init.body !== undefined) headers['Content-Type'] = 'application/json'
  if (init.token) headers.Authorization = `Token ${init.token}`
  const r = await fetch(`${SOCIAL_API}/${path}`, { method: init.method ?? 'GET', headers, body: init.body !== undefined ? JSON.stringify(init.body) : undefined })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new ApiError(r.status, (j && (j.error || j.state)) || `http_${r.status}`, j && typeof j === 'object' ? j : {})
  return j as T
}

export const ERRORS: Record<string, string> = {
  handle_shape: '3 to 15 characters: start with a letter, then letters, digits, "." or "_".',
  handle_reserved: 'That handle is reserved.',
  handle_not_allowed: 'That handle is not allowed.',
  handle_authority: 'Handles cannot contain support, admin, official, mod, staff or helpdesk, even inside a longer name. Only CADINU\'s own accounts use them.',
  team_team_cap: 'This wallet already holds 2 token-team ticks, the most one wallet can hold. Leave one first.',
  team_already_accepted: 'You already confirmed this role.',
  team_revoked: 'This invitation was withdrawn.',
  team_not_paid: 'This project info is not under review any more.',
  team_not_found: 'This invitation is not for this wallet.',
  wrong_signer: 'The signature came from another wallet. Sign with the wallet that is listed.',
  nonce_used_or_expired: 'That signature request expired. Try again.',
  handle_taken: 'That handle is taken.',
  handle_cooldown: 'You can change your handle once every 7 days.',
  not_owner: 'This wallet does not hold that NFT.',
  chain_unavailable: 'BNB Chain did not answer. Try again in a minute.',
  rate_limited: 'Too many tries. Wait a minute.',
  profile_off: 'This profile is off.',
}
export const errorText = (e: unknown) => {
  if (!(e instanceof ApiError)) return 'Network error. Try again.'
  // the API names the authority word it found, so the message says exactly what to drop
  if (e.code === 'handle_authority' && typeof e.body.word === 'string' && /^[a-z]{3,12}$/.test(e.body.word))
    return `Handles cannot contain "${e.body.word}", even inside a longer name: support, admin, official, mod, staff and helpdesk are kept so nobody can pose as CADINU. Brand words like "cadinu" are fine.`
  return ERRORS[e.code] ?? 'Something went wrong. Try again.'
}
